import React, {memo, useEffect, useState} from "react";
import {
  AssistantType,
  FlowNodeTypes,
  SettingParam,
  SettingsProps,
} from "@/types/node";
import {NodeSettings} from "../../../lib/SettingsBuilder";
import {cn} from "@/lib/utils";
import {useSelectedElements} from "@/hooks/useSelectedElements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {ActionTable} from "@/components/settings/Actions/SelectActionsTable";
import {FlowNodeData} from "@/types/node";
import {Action as ActionType} from "@/api/services";
import {Node, useReactFlow} from "@xyflow/react";
import {useNodeUpdater} from "@/hooks/useNodeUpdater";
import {useSave} from "@/contexts/SaveContext";
import {
  ActionTypeModel,
  CustomActionRecordModel,
  RealTimeBookingRecordModel,
  CallTransferRecordModel,
} from "@/api/models";
import {getSettingValueByKey} from "@/utils/index";
import {generateNodeSettingsFromAction} from "@/utils/actions";
import InitializeAction from "@/components/Drawers/InitializeAction";

export type Action =
  | CustomActionRecordModel
  | RealTimeBookingRecordModel
  | CallTransferRecordModel;

const ActionNodeTypesMapping = {
  [FlowNodeTypes.ACTION]: ActionTypeModel.CUSTOM,
  [FlowNodeTypes.TRANSFER]: ActionTypeModel.TRANSFER,
  [FlowNodeTypes.BOOKING]: ActionTypeModel.BOOKING,
};

const ActionsDialog = ({
  open,
  nodeType,
  selectedNode,
  setSelectedNode,
  setOpen,
  takeSnapshot,
}: {
  open: boolean;
  selectedNode: Node<FlowNodeData>;
  nodeType: FlowNodeTypes;
  setSelectedNode: (nodeId: string) => void;
  setOpen: (open: boolean) => void;
  takeSnapshot: () => void;
}) => {
  const {handleSave} = useSave();
  const {fitView} = useReactFlow();
  const {updateNodeSettingParam} = useNodeUpdater();

  const handleActionSelect = (action: Action) => {
    if (!action.action_id) return;
    const {nodeData, ...actionWithoutNodeData} = action;
    const newAction =
      action.type === ActionTypeModel.BOOKING ||
      action.type === ActionTypeModel.TRANSFER
        ? {...action}
        : {...nodeData, actionRecord: {...actionWithoutNodeData}};

    if (action.type === ActionTypeModel.CUSTOM) {
      const settings = generateNodeSettingsFromAction(
        newAction,
        ActionNodeTypesMapping[nodeType],
      );
      selectedNode.data.settings = settings;
    }
    updateNodeSettingParam(selectedNode.id, "action", newAction);

    setOpen(false);
    setSelectedNode(selectedNode.id);
    fitView({nodes: [selectedNode], duration: 1000});

    handleSave();
    takeSnapshot();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={e => {
        setSelectedNode(null);
        setOpen(false);
      }}
    >
      <DialogContent className="w-160 flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-primary font-semibold text-lg">
            Add Action
          </DialogTitle>
        </DialogHeader>
        <ActionTable
          handleActionSelect={handleActionSelect}
          onDialogClose={() => setOpen(false)}
          actionType={ActionNodeTypesMapping[nodeType]}
        />
      </DialogContent>
    </Dialog>
  );
};

const Settings: React.FC<SettingsProps> = ({
  selectedNode,
  takeSnapshot,
  assistantType,
  className,
  onSettingChange,
  saveOnChange = true,
}) => {
  const {setSelectedNode} = useSelectedElements();
  const {setBlockSave, processActionUpdate} = useSave();
  const [showActionsModal, setShowActionsModal] = useState(false);
  const {updateNodeSettingParam} = useNodeUpdater();
  const nodeType = selectedNode?.data.type;
  const isActionNode =
    nodeType === FlowNodeTypes.ACTION ||
    nodeType === FlowNodeTypes.TRANSFER ||
    nodeType === FlowNodeTypes.BOOKING;

  const getActionFromSettings = (node: Node<FlowNodeData>) =>
    getSettingValueByKey(node.data.settings, "action") as ActionType & {
      action_id?: string;
    };

  useEffect(() => {
    if (isActionNode) {
      const action = getActionFromSettings(selectedNode) as unknown as {
        actionRecord: Action;
      };
      const hasActionId = action?.actionRecord?.action_id;

      if (
        nodeType === FlowNodeTypes.TRANSFER ||
        nodeType === FlowNodeTypes.BOOKING ||
        (nodeType === FlowNodeTypes.ACTION && !hasActionId)
      ) {
        setShowActionsModal(true);
      } else {
        const settings = generateNodeSettingsFromAction(
          action,
          ActionNodeTypesMapping[nodeType],
        );
        selectedNode.data.settings = settings;
        updateNodeSettingParam(selectedNode.id, "action", action);
      }
    }
  }, [nodeType, setSelectedNode]);

  // Reset blockSave when selectedNode changes (settings panel closed/opened)
  useEffect(() => {
    if (selectedNode.id) return;
    setBlockSave(false);
  }, [selectedNode]);

  if (!selectedNode || !selectedNode.data?.settings) return null;

  const handleSettingsChange = (param: SettingParam, value: any) => {
    if (isActionNode) {
      const action = getActionFromSettings(selectedNode) as any;
      const update = processActionUpdate(param.key, value);
      updateNodeSettingParam(selectedNode.id, "action", {
        ...action,
        actionRecord: {
          ...action.actionRecord,
          ...update,
        },
        [param.key]: value,
      });
    }
    onSettingChange?.(param, value);
  };

  return (isActionNode && showActionsModal) ||
    nodeType === FlowNodeTypes.TRANSFER ||
    nodeType === FlowNodeTypes.BOOKING ? (
    <ActionsDialog
      open={showActionsModal}
      setOpen={setShowActionsModal}
      selectedNode={selectedNode}
      setSelectedNode={setSelectedNode}
      takeSnapshot={takeSnapshot}
      nodeType={nodeType}
    />
  ) : (
    <>
      <div
        className={cn(
          "drawer h-full w-110 bg-white border rounded-2xl relative z-[99999]",
          className,
        )}
        key={selectedNode.id}
      >
        <NodeSettings
          selectedNode={selectedNode}
          takeSnapshot={takeSnapshot}
          assistantType={assistantType || AssistantType.OUTBOUND}
          onSettingChange={handleSettingsChange}
          saveOnChange={saveOnChange}
        />
      </div>
      <InitializeAction selectedNode={selectedNode} />
    </>
  );
};

export default memo(Settings);
